import { useQueries } from '@tanstack/react-query';
import { useDeferredValue, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import { ApiError } from '../../lib/api-client';
import { CkEditorField } from '../../components/ui/CkEditorField';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { isPhoneFieldName, normalizePhoneInput, PHONE_INPUT_MAX_LENGTH, PHONE_INPUT_PATTERN, PHONE_INPUT_TITLE } from '../../lib/phone-input';
import { useToast } from '../../lib/toast';
import { fetchSettingsLookup } from './settings.api';
import { readGeneralSettingsImage, SETTINGS_RECORD_IMAGE_MAX_BYTES } from './general-settings.form';
import { settingsKeys } from './settings.queries';
import {
  buildSettingsPayload,
  getReferenceId,
  getValueAtPath,
  lookupRecordLabel,
  makeSettingsFormValues,
  validateSettingsForm,
} from './settings.utils';
import type {
  SettingsFormField,
  SettingsFormValue,
  SettingsModuleDefinition,
  SettingsRecord,
} from './types';

interface SettingsFormDrawerProps {
  definition: SettingsModuleDefinition;
  record?: SettingsRecord | null;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}

interface FieldControlProps {
  field: SettingsFormField;
  value: SettingsFormValue;
  error?: string | undefined;
  lookupRecords: SettingsRecord[];
  lookupLoading: boolean;
  lookupError: boolean;
  lookupSearch: string;
  lookupSelectedLabel: string;
  lookupSelectedOptions: Array<{ value: string; label: string }>;
  imageReading: boolean;
  onLookupSearch: (value: string) => void;
  onImageFile: (file: File) => void;
  onClearImage: () => void;
  onChange: (value: SettingsFormValue) => void;
}

const FieldControl = ({ field, value, error, lookupRecords, lookupLoading, lookupError, lookupSearch, lookupSelectedLabel, lookupSelectedOptions, imageReading, onLookupSearch, onImageFile, onClearImage, onChange }: FieldControlProps) => {
  const fieldId = `setting-${field.path.replaceAll('.', '-')}`;
  const optionLabelCache = useRef(new Map<string, string>());
  const fetchedOptions = field.options ?? lookupRecords.map((item) => ({
    value: field.lookupValue === 'label' ? lookupRecordLabel(item) : item.id,
    label: lookupRecordLabel(item),
  }));
  [...lookupSelectedOptions, ...fetchedOptions].forEach((option) => {
    if (option.label !== 'Loading charge...' && option.label !== 'Selected charge') {
      optionLabelCache.current.set(option.value, option.label);
    }
  });
  const persistentSelectedOptions = (Array.isArray(value) ? value : []).map((selectedValue) => ({
    value: selectedValue,
    label: optionLabelCache.current.get(selectedValue)
      ?? lookupSelectedOptions.find((option) => option.value === selectedValue)?.label
      ?? 'Loading charge...',
  }));
  const availableOptions = [...new Map(
    [...persistentSelectedOptions, ...lookupSelectedOptions, ...fetchedOptions].map((option) => [
      option.value,
      optionLabelCache.current.has(option.value)
        ? { ...option, label: optionLabelCache.current.get(option.value)! }
        : option,
    ]),
  ).values()];
  const phoneField = isPhoneFieldName(field.path);
  const common = {
    id: fieldId,
    value: typeof value === 'string' ? value : '',
    'aria-invalid': Boolean(error),
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onChange(phoneField ? normalizePhoneInput(event.target.value) : event.target.value),
  };

  if (field.type === 'checkbox-group') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <fieldset className={`settings-field settings-checkbox-group${field.fullWidth ? ' full' : ''}`} aria-invalid={Boolean(error)}>
        <legend>{field.label}{field.required ? <b aria-hidden="true"> *</b> : null}</legend>
        <div className="settings-checkbox-options">
          {(field.options ?? []).map((option) => (
            <label key={option.value} className="settings-checkbox-option">
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={(event) => onChange(event.target.checked
                  ? [...selected, option.value]
                  : selected.filter((item) => item !== option.value))}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        {field.help ? <small className="settings-field-hint">{field.help}</small> : null}
        {error ? <small className="settings-field-error" id={`${fieldId}-error`}>{error}</small> : null}
      </fieldset>
    );
  }

  return (
    <label className={`settings-field${field.fullWidth ? ' full' : ''}`} htmlFor={fieldId}>
      {field.type === 'textarea' ? null : <span>{field.label}{field.required ? <b aria-hidden="true"> *</b> : null}</span>}
      {field.type === 'image' ? (
        <div className="settings-image-field">
          <input
            id={fieldId}
            type="file"
            accept={field.accept}
            disabled={imageReading}
            aria-invalid={Boolean(error)}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onImageFile(file);
              event.target.value = '';
            }}
          />
          {typeof value === 'string' && value ? (
            <div className="settings-image-preview">
              <img src={value} alt={`${field.label} preview`} />
              <button type="button" className="button button-secondary" onClick={onClearImage} disabled={imageReading}>Clear</button>
            </div>
          ) : null}
          {imageReading ? <small className="settings-field-hint">Reading image...</small> : null}
        </div>
      ) : field.type === 'textarea' ? (
        <CkEditorField label={<>{field.label}{field.required ? <b aria-hidden="true"> *</b> : null}</>} value={common.value} onChange={onChange} {...(field.placeholder ? { placeholder: field.placeholder } : {})} />
      ) : field.type === 'multi-select' ? (
        <div className="settings-custom-multi-select">
          <SearchableSelect
            id={fieldId}
            multiple
            value={Array.isArray(value) ? value : []}
            options={availableOptions}
            placeholder={lookupLoading ? 'Loading choices...' : field.placeholder ?? `Select ${field.label.toLowerCase()}`}
            searchPlaceholder={`Search ${field.label.toLowerCase()}...`}
            searchValue={lookupSearch}
            searchable={field.lookupSearchable !== false}
            clearable
            ariaInvalid={Boolean(error)}
            disabled={lookupLoading && !availableOptions.length}
            emptyMessage={lookupLoading ? 'Loading choices...' : 'No matching charges found'}
            onSearchChange={onLookupSearch}
            onChange={onChange}
          />
          {Array.isArray(value) && value.length > 0 ? (
            <div className="settings-multi-select-chips" aria-label={`Selected ${field.label.toLowerCase()}`}>
              {value.map((selectedValue) => (
                <span className="settings-multi-select-chip" key={selectedValue}>
                  {availableOptions.find((option) => option.value === selectedValue)?.label ?? 'Selected charge'}
                  <button type="button" aria-label={`Remove ${availableOptions.find((option) => option.value === selectedValue)?.label ?? 'selected charge'}`} onClick={(event) => { event.stopPropagation(); onChange(value.filter((item) => item !== selectedValue)); }}>×</button>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : field.type === 'select' ? field.lookupResource ? (
        field.lookupSearchableSelect ? (
          <SearchableSelect
            id={fieldId}
            value={typeof value === 'string' ? value : ''}
            options={availableOptions}
            placeholder={lookupLoading ? 'Loading choices...' : field.placeholder ?? `Select ${field.label.toLowerCase()}`}
            searchPlaceholder={`Search ${field.label.toLowerCase()}...`}
            searchable
            ariaInvalid={Boolean(error)}
            disabled={lookupLoading && !availableOptions.length}
            emptyMessage={lookupLoading ? 'Loading choices...' : `No ${field.label.toLowerCase()} found`}
            onSearchChange={onLookupSearch}
            onChange={onChange}
          />
        ) : <div className={`settings-remote-lookup${field.lookupSearchable === false ? ' settings-remote-lookup-single' : ''}`}>
          {field.lookupSearchable === false ? null : (
            <input type="search" value={lookupSearch} onChange={(event) => onLookupSearch(event.target.value)} placeholder="Type to search..." aria-label={`Search ${field.label.toLowerCase()}`} />
          )}
          <select {...common} disabled={lookupLoading && !lookupRecords.length}>
            <option value="">{lookupLoading ? 'Loading choices...' : field.placeholder ?? `Select ${field.label.toLowerCase()}`}</option>
            {typeof value === 'string' && value && !availableOptions.some((option) => option.value === value)
              ? <option value={value}>{lookupSelectedLabel || 'Selected record'}</option>
              : null}
            {availableOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
      ) : (
        <select {...common}>
          <option value="">{field.placeholder ?? `Select ${field.label.toLowerCase()}`}</option>
          {availableOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      ) : (
        <input
          {...common}
          type={phoneField ? 'tel' : field.type}
          placeholder={field.placeholder}
          min={field.min}
          max={field.max}
          step={field.step}
          maxLength={phoneField ? PHONE_INPUT_MAX_LENGTH : undefined}
          inputMode={phoneField ? 'numeric' : undefined}
          pattern={phoneField ? PHONE_INPUT_PATTERN : undefined}
          title={phoneField ? PHONE_INPUT_TITLE : undefined}
        />
      )}
      {field.help ? <small className="settings-field-hint">{field.help}</small> : null}
      {lookupError ? <small className="settings-field-error">Choices could not be loaded.</small> : null}
      {error ? <small className="settings-field-error" id={`${fieldId}-error`}>{error}</small> : null}
    </label>
  );
};

export const SettingsFormDrawer = ({ definition, record, isPending, onClose, onSubmit }: SettingsFormDrawerProps) => {
  const { showToast } = useToast();
  const [values, setValues] = useState(() => makeSettingsFormValues(definition.fields, record));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lookupSearches, setLookupSearches] = useState<Record<string, string>>({});
  const [imageReading, setImageReading] = useState<Record<string, boolean>>({});
  const imageGenerations = useRef<Record<string, number>>({});
  const deferredLookupSearches = useDeferredValue(lookupSearches);
  const lookupFields = useMemo(
    () => definition.fields.filter((field) => field.lookupResource),
    [definition.fields],
  );
  const lookupQueries = useQueries({
    queries: lookupFields.map((field) => {
      const resource = field.lookupResource!;
      const parentValue = field.lookupParentPath ? values[field.lookupParentPath] : undefined;
      const parentId = typeof parentValue === 'string' && parentValue ? parentValue : undefined;
      const search = deferredLookupSearches[field.path]?.trim() ?? '';
      const fieldValue = values[field.path];
      const selectedIds = Array.isArray(fieldValue) ? fieldValue.join(',') : undefined;
      return {
        queryKey: [...settingsKeys.lookup(resource), 'field', field.path, 'search', search, 'parent', parentId ?? '', 'selected', selectedIds ?? ''],
        queryFn: () => fetchSettingsLookup(resource, {
          ...(search ? { search } : {}),
          ...(parentId ? { parentId } : {}),
          ...(selectedIds ? { selectedIds } : {}),
        }),
        enabled: !field.lookupParentPath || Boolean(parentId),
        staleTime: 5 * 60_000,
      };
    }),
  });
  const lookups = Object.fromEntries(lookupFields.map((field, index) => [field.path, lookupQueries[index]]));
  const sections = [...new Set(definition.fields.map((field) => field.section))];
  const isReadingImage = Object.values(imageReading).some(Boolean);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' && !isPending && !isReadingImage) onClose(); };
    document.body.classList.add('modal-open');
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isPending, isReadingImage, onClose]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (isReadingImage) return;
    const nextErrors = validateSettingsForm(definition.fields, values);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      document.getElementById(`setting-${Object.keys(nextErrors)[0]?.replaceAll('.', '-')}`)?.focus();
      return;
    }
    try {
      await onSubmit(buildSettingsPayload(definition.fields, values));
      showToast(`${definition.singular} saved successfully.`, 'success');
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : `The ${definition.singular} could not be saved.`, 'error');
    }
  };

  const readImage = async (field: SettingsFormField, file: File) => {
    const generation = (imageGenerations.current[field.path] ?? 0) + 1;
    imageGenerations.current[field.path] = generation;
    setImageReading((current) => ({ ...current, [field.path]: true }));
    setErrors((current) => ({ ...current, [field.path]: '' }));
    try {
      const dataUrl = await readGeneralSettingsImage(file, SETTINGS_RECORD_IMAGE_MAX_BYTES);
      if (imageGenerations.current[field.path] !== generation) return;
      setValues((current) => ({ ...current, [field.path]: dataUrl }));
    } catch (error) {
      if (imageGenerations.current[field.path] !== generation) return;
      setErrors((current) => ({
        ...current,
        [field.path]: error instanceof Error ? error.message : 'The image could not be read.',
      }));
    } finally {
      if (imageGenerations.current[field.path] === generation) {
        setImageReading((current) => ({ ...current, [field.path]: false }));
      }
    }
  };

  return (
    <div className="settings-drawer-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !isPending && !isReadingImage) onClose();
    }}>
      <aside className="settings-drawer" role="dialog" aria-modal="true" aria-labelledby="settings-form-title">
        <header className="settings-drawer-header">
          <div>
            <p className="eyebrow">{record ? 'Edit configuration' : 'New configuration'}</p>
            <h2 id="settings-form-title">{record ? `Edit ${definition.singular}` : `Add ${definition.singular}`}</h2>
            <p>Required fields are marked with an asterisk.</p>
          </div>
          <button className="settings-drawer-close" type="button" onClick={onClose} disabled={isPending || isReadingImage} aria-label="Close editor">x</button>
        </header>
        <form className={`settings-drawer-form settings-drawer-form-${definition.slug}`} onSubmit={(event) => void submit(event)} noValidate>
          <div className="settings-form-body">
            {sections.map((section) => (
              <fieldset className="settings-form-section" key={section}>
                <legend>{section}</legend>
                <div className="settings-form-grid">
                  {definition.fields.filter((field) => field.section === section).map((field) => {
                    const lookup = field.lookupResource ? lookups[field.path] : undefined;
                    return (
                      <FieldControl
                        key={field.path}
                        field={field}
                        value={values[field.path] ?? (field.type === 'multi-select' ? [] : '')}
                        error={errors[field.path] || undefined}
                        lookupRecords={(lookup?.data ?? []).filter((item) => item.id !== record?.id)}
                        lookupLoading={lookup?.isLoading ?? false}
                        lookupError={lookup?.isError ?? false}
                        lookupSelectedLabel={(() => {
                          if (!record || !field.lookupResource) return '';
                          const reference = getValueAtPath(record, field.path);
                          return typeof reference === 'object' && reference !== null
                            ? lookupRecordLabel(reference as SettingsRecord)
                            : '';
                        })()}
                        lookupSelectedOptions={(() => {
                          if (!record || field.type !== 'multi-select') return [];
                          const references = getValueAtPath(record, field.path);
                          if (!Array.isArray(references)) return [];
                          return references.flatMap((reference) => {
                            const id = getReferenceId(reference);
                            if (!id) return [];
                            if (typeof reference !== 'object' || reference === null) {
                              return [{ value: id, label: lookup?.isLoading ? 'Loading charge...' : 'Selected charge' }];
                            }
                            return [{ value: id, label: lookupRecordLabel(reference as SettingsRecord) }];
                          });
                        })()}
                        lookupSearch={lookupSearches[field.path] ?? ''}
                        imageReading={imageReading[field.path] ?? false}
                        onLookupSearch={(value) => field.lookupResource && setLookupSearches((current) => ({ ...current, [field.path]: value }))}
                        onImageFile={(file) => void readImage(field, file)}
                        onClearImage={() => {
                          imageGenerations.current[field.path] = (imageGenerations.current[field.path] ?? 0) + 1;
                          setValues((current) => ({ ...current, [field.path]: '' }));
                          setErrors((current) => ({ ...current, [field.path]: '' }));
                        }}
                        onChange={(value) => {
                          setValues((current) => {
                            const next = { ...current, [field.path]: value };
                            definition.fields
                              .filter((candidate) => candidate.lookupParentPath === field.path)
                              .forEach((candidate) => { next[candidate.path] = ''; });
                            return next;
                          });
                          if (errors[field.path]) setErrors((current) => ({ ...current, [field.path]: '' }));
                        }}
                      />
                    );
                  })}
                </div>
              </fieldset>
            ))}
          </div>
          <footer className="settings-drawer-actions">
            <button className="button button-secondary" type="button" onClick={onClose} disabled={isPending || isReadingImage}>Cancel</button>
            <button className="button button-primary" type="submit" disabled={isPending || isReadingImage}>
              {isReadingImage ? 'Reading image...' : isPending ? 'Saving...' : record ? 'Save changes' : `Add ${definition.singular}`}
            </button>
          </footer>
        </form>
      </aside>
    </div>
  );
};
